// components/ContributorBottomNav.jsx — Anamoria SPA
// v1.4 — NEW-163 Increment B (2026-10-30): the navigation targets and the Safari-safe photo
//   picker logic now come from the shared helper utils/contributorCapture.js (captureTargets,
//   openPhotoPicker, photoFileFromChange) so the nav and the group-note Respond row
//   (GroupNoteBanner v1.2) share one definition. NO behaviour change: same four routes, same
//   flag gate, the file input ref stays in this component (WebKit user-gesture chain — spec C-a).
//   Pinned by ContributorBottomNav.test.jsx, written against v1.3 before this refactor.
// v1.3 — NEW-121: fix — video button wasn't actually flag-gated (2026-09-27)
//
// Changes from v1.2:
//   - v1.2's changelog claimed the Video button was flag-gated, but the
//     `videoEnabled` prop was never destructured and the button rendered
//     unconditionally. Fixed: added `videoEnabled = false` to props, wrapped
//     the button in `{videoEnabled && (...)}`. All 5 call sites already
//     passed the prop correctly — this was the only broken piece.
//
// v1.2 — NEW-121: 4th button, Video (2026-09-27)
//
// Changes from v1.1:
//   - Added a "Video" button (RecordIcon/WriteIcon/PhotoIcon's existing sibling
//     VideoIcon, from ./BrandIcons — no new icon work) navigating to
//     {navBase}/{spaceId}/video, same prefix/mode-aware pattern as the other
//     three buttons. Route itself is flag-gated (contributor_video_enabled) —
//     see ContributorVideoPage.jsx. No CSS change: BottomNav.module.css's
//     .nav already uses flex: 1 per .btn (confirmed by this file's own v1.0
//     comment below re: 4→3), so 3→4 redistributes automatically.
//
// v1.1 — Account-link access mode: mode-aware nav prefix (May 31, 2026)
//
// Changes from v1.0:
//   - Added `mode` prop ('session' | 'linked', default 'session' → no
//     regression). Navigation targets are prefix-aware: '/contributing' in
//     linked mode, '/contribute' in session mode. Record/Write/Photo otherwise
//     identical.
//
// v1.0 — Session 3 (April 19, 2026)
//
// Contributor-side bottom navigation. 3 buttons (Record, Write, Photo).
// No Invite button — contributors cannot invite (only space owners can, per
// Session 3 Plan v1.1 decision V: contributors see each other's memories only,
// no ownership-level actions).
//
// REUSES BottomNav.module.css (per Session 3 Plan v1.1 decision B2 — new
// component, shared CSS module). The .nav class uses flex: 1 on each .btn
// so dropping from 4 to 3 buttons redistributes space automatically — no
// CSS changes required.
//
// Differences from owner BottomNav.jsx v1.1:
//   - No Invite button
//   - Navigates to /contribute/{spaceId}/{record|write|photo|video} instead of
//     /spaces/{spaceId}/{record|write|photo|video}
//   - Otherwise identical: same icons, same Safari-safe photo picker pattern,
//     same CSS classes
//
// Props:
//   spaceId   — current space UUID (used for navigation)
//   activeTab — 'record' | 'write' | 'photo' | 'video'
//
// Safari user-gesture note (inherited from owner):
//   Photo button opens file picker synchronously in the click handler. File
//   selection triggers navigate(...) with the File passed via location.state.
//   Navigating first then calling .click() would break Safari's user-activation
//   chain (WebKit enforces .click() must be called in the same task as the
//   user gesture). See owner BottomNav.jsx v1.1 comment block for references.

import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { RecordIcon, WriteIcon, PhotoIcon, VideoIcon } from './BrandIcons';
import styles from './BottomNav.module.css';
import { captureTargets, openPhotoPicker, photoFileFromChange } from '../utils/contributorCapture';   // v1.4

export default function ContributorBottomNav({ spaceId, activeTab = 'record', mode = 'linked', videoEnabled = false }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  // Track B (B-2): linked is the only contributor rail; nav always uses /contributing.
  const navBase = mode === 'linked' ? '/contributing' : '/contribute';
  // v1.4: the four routes come from the shared helper (no ?note= here — the nav is not a response).
  const to = captureTargets({ spaceId, navBase });

  // ─── Photo picker handlers ─────────────────────────────────
  // Identical behavior to owner BottomNav: file picker opens synchronously
  // to preserve Safari user-gesture chain; file is passed via location.state.
  // v1.4: logic shared via utils/contributorCapture.js; the input ref stays here.

  function handlePhotoClick() {
    openPhotoPicker(fileInputRef.current);
  }

  function handlePhotoFileChange(e) {
    const file = photoFileFromChange(e);
    if (!file) return; // cancel → stay on feed
    navigate(to.photo, { state: { file } });
  }

  return (
    <nav className={styles.nav} aria-label="Contributor navigation">

      {/* Record — primary action */}
      <button
        className={`${styles.btn} ${styles.btnPrimary} ${activeTab === 'record' ? styles.active : ''}`}
        onClick={() => navigate(to.record)}
        title="Record a voice memory"
        aria-label="Record a voice memory"
      >
        <span className={styles.icon}>
          <RecordIcon />
        </span>
        <span className={styles.label}>Record</span>
      </button>

      {/* Write */}
      <button
        className={`${styles.btn} ${activeTab === 'write' ? styles.active : ''}`}
        onClick={() => navigate(to.write)}
        title="Write a memory"
        aria-label="Write a memory"
      >
        <span className={styles.icon}>
          <WriteIcon />
        </span>
        <span className={styles.label}>Write</span>
      </button>

      {/* Photo — opens file picker in same user-gesture task (Safari-safe) */}
      <button
        className={`${styles.btn} ${activeTab === 'photo' ? styles.active : ''}`}
        onClick={handlePhotoClick}
        title="Add a photo"
        aria-label="Add a photo"
      >
        <span className={styles.icon}>
          <PhotoIcon />
        </span>
        <span className={styles.label}>Photo</span>
      </button>

      {/* Hidden file input — clicked programmatically from Photo button above. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className={styles.fileInput}
        onChange={handlePhotoFileChange}
      />

      {/* Video — NEW-121, flag-gated route (contributor_video_enabled); record-only, no library picker */}
      {videoEnabled && (
        <button
          className={`${styles.btn} ${activeTab === 'video' ? styles.active : ''}`}
          onClick={() => navigate(to.video)}
          title="Record a video"
          aria-label="Record a video"
        >
          <span className={styles.icon}>
            <VideoIcon />
          </span>
          <span className={styles.label}>Video</span>
        </button>
      )}

    </nav>
  );
}
